import {
    CheckboxInspectionItem,
    DateInputInspectionItem,
    InspectionItem,
    NotesInspectionItem,
    NumericRangeInspectionItem,
    TrafficLightInspectionItem,
    TriStateInspectionItem,
} from './inspection.model';
import {
    CheckboxInspectionTemplateItem,
    DateInputInspectionTemplateItem,
    InspectionTemplateItem,
    NotesInspectionTemplateItem,
    NumericRangeInspectionTemplateItem,
    TrafficLightInspectionTemplateItem,
    TriStateInspectionTemplateItem,
} from './inspection-template.model';
import { InspectionItemType } from './inspection-item-type.enum';

/**
 * Type guard to check if a template item is a TrafficLight type
 */
export function isTrafficLightTemplateItem(item: InspectionTemplateItem): item is TrafficLightInspectionTemplateItem {
    return item.itemType === InspectionItemType.TrafficLight;
}

/**
 * Type guard to check if a template item is a NumericRange type
 */
export function isNumericRangeTemplateItem(item: InspectionTemplateItem): item is NumericRangeInspectionTemplateItem {
    return item.itemType === InspectionItemType.NumericRange;
}

/**
 * Type guard to check if a template item is a Checkbox type
 */
export function isCheckboxTemplateItem(item: InspectionTemplateItem): item is CheckboxInspectionTemplateItem {
    return item.itemType === InspectionItemType.Checkbox;
}

/**
 * Type guard to check if a template item is a Notes type
 */
export function isNotesTemplateItem(item: InspectionTemplateItem): item is NotesInspectionTemplateItem {
    return item.itemType === InspectionItemType.Notes;
}

/**
 * Type guard to check if a template item is a DateInput type
 */
export function isDateInputTemplateItem(item: InspectionTemplateItem): item is DateInputInspectionTemplateItem {
    return item.itemType === InspectionItemType.DateInput;
}

/**
 * Type guard to check if a template item is a TriState type
 */
export function isTriStateTemplateItem(item: InspectionTemplateItem): item is TriStateInspectionTemplateItem {
    return item.itemType === InspectionItemType.TriState;
}

/**
 * Type guard to check if an inspection item is a TrafficLight type
 */
export function isTrafficLightItem(item: InspectionItem): item is TrafficLightInspectionItem {
    return item.itemType === InspectionItemType.TrafficLight;
}

/**
 * Type guard to check if an inspection item is a NumericRange type
 */
export function isNumericRangeItem(item: InspectionItem): item is NumericRangeInspectionItem {
    return item.itemType === InspectionItemType.NumericRange;
}

/**
 * Type guard to check if an inspection item is a Checkbox type
 */
export function isCheckboxItem(item: InspectionItem): item is CheckboxInspectionItem {
    return item.itemType === InspectionItemType.Checkbox;
}

/**
 * Type guard to check if an inspection item is a Notes type
 */
export function isNotesItem(item: InspectionItem): item is NotesInspectionItem {
    return item.itemType === InspectionItemType.Notes;
}

/**
 * Type guard to check if an inspection item is a DateInput type
 */
export function isDateInputItem(item: InspectionItem): item is DateInputInspectionItem {
    return item.itemType === InspectionItemType.DateInput;
}

/**
 * Type guard to check if an inspection item is a TriState type
 */
export function isTriStateItem(item: InspectionItem): item is TriStateInspectionItem {
    return item.itemType === InspectionItemType.TriState;
}

/**
 * Determines whether an inspection item has been responded to by the technician.
 *
 * Each item type persists its response on a different field, so completeness is
 * evaluated per type. This is the single source of truth for "answered" so that the
 * web (section summary store) and mobile (section accordion) section-completion
 * indicators agree on what counts as a response. A numeric measurement of `0` is a
 * valid response, so emptiness is tested with a null check rather than truthiness.
 * @param item - The runtime inspection item to evaluate
 * @returns True when the item carries a response appropriate to its type
 */
export function isInspectionItemAnswered(item: InspectionItem): boolean {
    if (isTrafficLightItem(item)) {
        return !!item.resultId;
    }
    if (isNumericRangeItem(item)) {
        return item.actualValue != null;
    }
    if (isCheckboxItem(item)) {
        return item.value != null;
    }
    if (isNotesItem(item)) {
        return !!item.content && item.content.trim().length > 0;
    }
    if (isDateInputItem(item)) {
        return item.dateValue != null;
    }
    if (isTriStateItem(item)) {
        // NotApplicable is a deliberate answer, so only an untouched item counts as unanswered
        return item.value != null;
    }
    return false;
}

/**
 * Gets a human-readable label for the item type
 * @param itemType - The inspection item type
 * @returns A descriptive label for the item type
 */
export function getItemTypeLabel(itemType: InspectionItemType): string {
    switch (itemType) {
        case InspectionItemType.TrafficLight:
            return 'Traffic Light (Pass/Warning/Fail)';
        case InspectionItemType.NumericRange:
            return 'Numeric Measurement';
        case InspectionItemType.Checkbox:
            return 'Yes/No';
        case InspectionItemType.Notes:
            return 'Notes';
        case InspectionItemType.DateInput:
            return 'Date Input';
        case InspectionItemType.TriState:
            return 'Yes/N/A/No';
        default:
            return 'Unknown';
    }
}

/**
 * Gets an icon name for the item type (for use with icon libraries)
 * @param itemType - The inspection item type
 * @returns An icon name representing the item type
 */
export function getItemTypeIcon(itemType: InspectionItemType): string {
    switch (itemType) {
        case InspectionItemType.TrafficLight:
            return 'traffic-light';
        case InspectionItemType.NumericRange:
            return 'ruler';
        case InspectionItemType.Checkbox:
            return 'check-square';
        case InspectionItemType.Notes:
            return 'file-text';
        case InspectionItemType.DateInput:
            return 'calendar';
        case InspectionItemType.TriState:
            return 'sliders';
        default:
            return 'help-circle';
    }
}
